import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
} from "remotion";
import { loadFont as loadDev } from "@remotion/google-fonts/NotoSansDevanagari";

const { fontFamily: devFont } = loadDev("normal", {
  weights: ["500", "700"],
  subsets: ["devanagari", "latin"],
});

// 3:4 reel caption canvas (matches the 960×1280 base for overlay=0:0 alignment)
const W = 960;
const H = 1280;

// roller geometry (ported from KamakhyaPlayer lyric roller)
const LINE_GAP = 92;      // vertical distance between lines
const ROLLER_W = 860;     // roller box width
const ROLLER_H = 360;     // roller box height
const ANCHOR_Y = 190;     // current line's y inside the box
const CENTER_Y = 900;     // box top placed so the current line sits ~lower third
const SHIFT_DUR = 0.4;    // seconds the line-roll takes, ENDING at the next line's first word

type Line = { text: string; start: number; end: number };

export const ReelRollerCaption: React.FC<{
  lines?: Line[];
  durationInFrames?: number;
}> = ({ lines = [] }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const t = frame / fps;

  if (!lines.length) return <AbsoluteFill />;

  // current fractional line position
  let cur = 0;
  for (let i = 0; i < lines.length; i++) {
    if (t >= lines[i].start) cur = i;
    else break;
  }
  const nextStart = cur + 1 < lines.length ? lines[cur + 1].start : lines[cur].end + 99;
  const shiftStart = Math.max(lines[cur].start, nextStart - SHIFT_DUR);
  let frac = 0;
  if (t > shiftStart && nextStart > shiftStart) frac = Math.min(1, (t - shiftStart) / (nextStart - shiftStart));
  const eased = frac * frac * (3 - 2 * frac); // smoothstep
  const pos = cur + eased;

  // visibility: hidden before first line; fade out in instrumental gaps (>1.4s)
  const firstStart = lines[0].start;
  const curEnd = lines[cur].end;
  const nextS2 = cur + 1 < lines.length ? lines[cur + 1].start : curEnd;
  const GAP_FADE = 0.5;
  let op = 1;
  if (t < firstStart) {
    op = interpolate(t, [firstStart - 0.4, firstStart], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  } else if (t > curEnd && nextS2 - curEnd > 1.4) {
    const outDone = curEnd + GAP_FADE;
    const inStart = nextS2 - GAP_FADE;
    if (t <= outDone) op = interpolate(t, [curEnd, outDone], [1, 0], { extrapolateRight: "clamp" });
    else if (t >= inStart) op = interpolate(t, [inStart, nextS2], [0, 1], { extrapolateLeft: "clamp" });
    else op = 0;
  }

  return (
    <AbsoluteFill>
      <div
        style={{
          position: "absolute",
          left: (W - ROLLER_W) / 2,
          top: CENTER_Y - ANCHOR_Y,
          width: ROLLER_W,
          height: ROLLER_H,
          perspective: 760,
          opacity: op,
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,1) 26%, rgba(0,0,0,1) 74%, transparent 100%)",
          maskImage:
            "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,1) 26%, rgba(0,0,0,1) 74%, transparent 100%)",
        }}
      >
        {lines.map((ln, i) => {
          const rel = i - pos; // 0 = current line
          if (rel < -1.6 || rel > 1.6) return null;
          const y = ANCHOR_Y + rel * LINE_GAP;
          const ar = Math.abs(rel);
          const opacity = interpolate(ar, [0, 1, 1.6], [1, 0.42, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
          const isCur = ar < 0.5;
          const scale = interpolate(ar, [0, 1], [1, 0.88], { extrapolateRight: "clamp" });
          const rotX = interpolate(rel, [-1.6, 0, 1.6], [26, 0, -24]); // drum curve
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                top: y,
                textAlign: "center",
                transform: `translateY(-50%) rotateX(${rotX}deg) scale(${scale})`,
                transformOrigin: "center center",
                opacity,
              }}
            >
              <span
                style={{
                  fontFamily: devFont,
                  fontWeight: isCur ? 700 : 500,
                  fontSize: isCur ? 46 : 40,
                  color: isCur ? "#ffe7b0" : "#ffffff",
                  letterSpacing: 0.3,
                  textShadow: isCur
                    ? "0 3px 22px rgba(0,0,0,0.95), 0 0 30px rgba(255,200,110,0.55)"
                    : "0 2px 14px rgba(0,0,0,0.9)",
                }}
              >
                {ln.text}
              </span>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
